import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, LogOut, SlidersHorizontal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function stepColor(step, completato) {
  if (completato) return "rgb(0,255,0)";
  if (step.value) return "#f5c518";
  return "#e5e7eb";
}

export default function AdminOrdini() {
  const navigate = useNavigate();
  const token = localStorage.getItem("vd_token");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ numero_ordine: "", email: "", cliente_nome: "" });
  const [subs, setSubs] = useState({ "1a": true, "1b": true, "1c": true, "2a": true, "2b": true, "2c": true });
  const SUB_LABELS = {
    "1a": "In produzione infissi",
    "1b": "In produzione falegnameria",
    "1c": "In produzione vetrate",
    "2a": "Installazione infissi",
    "2b": "Installazione falegnameria",
    "2c": "Installazione vetrate",
  };
  const [inputs, setInputs] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editSubs, setEditSubs] = useState({});

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ordini`, authHeader);
      setOrders(data || []);
    } catch (e) {
      if (e.response && e.response.status === 401) {
        localStorage.removeItem("vd_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const creaOrdine = async () => {
    if (!form.numero_ordine.trim()) return toast.error("Inserisci il Numero Ordine");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Inserisci un'email valida");
    const applicable = Object.keys(subs).filter((k) => subs[k]);
    try {
      await axios.post(`${API}/admin/ordini`, { ...form, applicable }, authHeader);
      toast.success("Ordine creato");
      setForm({ numero_ordine: "", email: "", cliente_nome: "" });
      setSubs({ "1a": true, "1b": true, "1c": true, "2a": true, "2b": true, "2c": true });
      load();
    } catch (e) {
      toast.error("Errore nella creazione dell'ordine");
    }
  };

  const confermaStep = async (orderId, stepKey) => {
    const key = `${orderId}-${stepKey}`;
    const value = inputs[key] || "";
    if (!value) return toast.error("Inserisci la data/settimana prima di confermare");
    try {
      await axios.post(`${API}/admin/ordini/${orderId}/step`, { step_key: stepKey, value }, authHeader);
      toast.success("Step confermato");
      load();
    } catch (e) {
      toast.error("Errore nell'aggiornamento dello step");
    }
  };

  const apriModifica = (o) => {
    const cur = {};
    Object.keys(SUB_LABELS).forEach((k) => { cur[k] = o.steps.some((s) => s.key === k); });
    setEditSubs(cur);
    setEditingId(o.id);
  };

  const salvaFasi = async (orderId) => {
    const applicable = Object.keys(editSubs).filter((k) => editSubs[k]);
    try {
      await axios.post(`${API}/admin/ordini/${orderId}/applicable`, { applicable }, authHeader);
      toast.success("Fasi aggiornate");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error("Errore nell'aggiornamento delle fasi");
    }
  };

  const eliminaOrdine = async (orderId) => {
    if (!window.confirm("Eliminare questo ordine?")) return;
    try {
      await axios.delete(`${API}/admin/ordini/${orderId}`, authHeader);
      load();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const logout = () => {
    localStorage.removeItem("vd_token");
    localStorage.removeItem("vd_name");
    navigate("/admin/login");
  };

  const anyValue = (o) => o.steps.some((s) => s.value);

  return (
    <div data-testid="admin-ordini-page" className="min-h-screen bg-[#f4f2ee] text-[#26241f]">
      <div className="border-b border-black/10 bg-white">
        <div className="max-w-[1300px] mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="font-serif text-xl">Gestione Ordini</h1>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="text-sm border border-black/20 px-4 py-2 hover:bg-black/5" data-testid="admin-back-dashboard">
              Dashboard
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm border border-black/20 px-4 py-2 hover:bg-black/5" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1300px] mx-auto px-6 py-8">
        <div className="bg-white border border-black/10 p-6 mb-8">
          <h2 className="text-sm uppercase tracking-wide mb-4">Crea nuovo ordine</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <input data-testid="ordine-numero" value={form.numero_ordine} onChange={(e) => setForm({ ...form, numero_ordine: e.target.value })} placeholder="Numero Ordine" className="bg-white border border-black/15 px-3 py-2.5 focus:outline-none focus:border-teal-600" />
            <input data-testid="ordine-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email cliente" className="bg-white border border-black/15 px-3 py-2.5 focus:outline-none focus:border-teal-600" />
            <input data-testid="ordine-nome" value={form.cliente_nome} onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })} placeholder="Nome cliente (facoltativo)" className="bg-white border border-black/15 px-3 py-2.5 focus:outline-none focus:border-teal-600" />
            <button data-testid="ordine-crea" onClick={creaOrdine} className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-4 py-2.5 text-sm uppercase tracking-wide hover:bg-teal-700">
              <Plus className="w-4 h-4" /> Crea
            </button>
          </div>
          <div className="mt-5 pt-5 border-t border-black/10">
            <p className="text-xs uppercase tracking-wide text-foreground/60 mb-3">Fasi applicabili a questo ordine</p>
            <div className="mb-3 flex items-start gap-2 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="fasi-avviso">
              <span className="font-semibold">⚠ Ricorda:</span>
              <span>Deseleziona le fasi che <strong>non</strong> riguardano questo ordine. Le fasi lasciate spuntate compariranno al cliente e nelle email di avanzamento.</span>
            </div>
            <p className="text-xs text-foreground/50 mb-3">Step 0 (Ordine confermato) e Step 3 (Ordine completato) sono sempre inclusi. Seleziona solo le fasi che riguardano questo ordine.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.keys(SUB_LABELS).map((k) => (
                <label key={k} data-testid={`sub-step-label-${k}`} className="flex items-center gap-2 text-sm border border-black/15 px-3 py-2 cursor-pointer hover:bg-black/5">
                  <input
                    type="checkbox"
                    data-testid={`sub-step-${k}`}
                    checked={subs[k]}
                    onChange={(e) => setSubs({ ...subs, [k]: e.target.checked })}
                    className="w-4 h-4 accent-teal-600"
                  />
                  <span className="text-xs font-semibold">Step {k}</span>
                  <span className="text-foreground/70">{SUB_LABELS[k]}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-foreground/60"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento...</div>
        ) : orders.length === 0 ? (
          <p className="text-foreground/60">Nessun ordine presente.</p>
        ) : (
          <div className="space-y-6" data-testid="admin-ordini-list">
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-black/10 p-6" data-testid={`ordine-card-${o.numero_ordine}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div>
                    <div className="font-serif text-lg">{o.numero_ordine}</div>
                    <div className="text-sm text-foreground/60">{o.email}{o.cliente_nome ? ` · ${o.cliente_nome}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-3 py-1 rounded-full" style={{ background: o.completato ? "rgb(0,255,0)" : anyValue(o) ? "#f5c518" : "#e5e7eb" }} data-testid={`ordine-stato-${o.numero_ordine}`}>
                      {o.completato ? "Completato" : anyValue(o) ? "In lavorazione" : "Non avviato"}
                    </span>
                    <button onClick={() => (editingId === o.id ? setEditingId(null) : apriModifica(o))} className="inline-flex items-center gap-1.5 text-sm border border-black/20 px-3 py-1.5 hover:bg-black/5" data-testid={`ordine-modifica-fasi-${o.numero_ordine}`}>
                      <SlidersHorizontal className="w-4 h-4" /> Modifica fasi
                    </button>
                    <button onClick={() => eliminaOrdine(o.id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`ordine-elimina-${o.numero_ordine}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {editingId === o.id && (
                  <div className="mb-4 p-4 border border-teal-600/40 bg-teal-50/40" data-testid={`ordine-fasi-panel-${o.numero_ordine}`}>
                    <p className="text-xs uppercase tracking-wide text-foreground/60 mb-3">Fasi applicabili — Step 0 e 3 sempre inclusi</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {Object.keys(SUB_LABELS).map((k) => (
                        <label key={k} className="flex items-center gap-2 text-sm border border-black/15 bg-white px-3 py-2 cursor-pointer hover:bg-black/5">
                          <input
                            type="checkbox"
                            data-testid={`ordine-${o.numero_ordine}-editsub-${k}`}
                            checked={!!editSubs[k]}
                            onChange={(e) => setEditSubs({ ...editSubs, [k]: e.target.checked })}
                            className="w-4 h-4 accent-teal-600"
                          />
                          <span className="text-xs font-semibold">Step {k}</span>
                          <span className="text-foreground/70">{SUB_LABELS[k]}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-4 flex gap-3">
                      <button onClick={() => salvaFasi(o.id)} className="bg-teal-600 text-white px-4 py-2 text-sm uppercase tracking-wide hover:bg-teal-700" data-testid={`ordine-salva-fasi-${o.numero_ordine}`}>
                        Salva fasi
                      </button>
                      <button onClick={() => setEditingId(null)} className="border border-black/20 px-4 py-2 text-sm uppercase tracking-wide hover:bg-black/5">
                        Annulla
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {o.steps.map((s) => {
                    const key = `${o.id}-${s.key}`;
                    const on = o.completato || !!s.value;
                    return (
                      <div key={s.key} className="border p-3" style={{ borderColor: on ? stepColor(s, o.completato) : "#e5e7eb", background: on ? `${stepColor(s, o.completato)}22` : "#fff" }}>
                        <div className="text-xs font-semibold mb-1">Step {s.key}</div>
                        <div className="text-sm mb-2 leading-tight">{s.label}</div>
                        <input
                          type={s.input === "week" ? "week" : "date"}
                          data-testid={`ordine-${o.numero_ordine}-input-${s.key}`}
                          defaultValue={s.value || ""}
                          onChange={(e) => setInputs({ ...inputs, [key]: e.target.value })}
                          className="w-full border border-black/15 px-2 py-1 text-xs mb-2"
                        />
                        <button data-testid={`ordine-${o.numero_ordine}-conferma-${s.key}`} onClick={() => confermaStep(o.id, s.key)} className="w-full text-xs bg-[#26241f] text-white px-2 py-1.5 hover:bg-black">
                          Conferma
                        </button>
                        {s.value_fmt && <div className="text-[11px] text-foreground/50 mt-1">{s.value_fmt}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
